import { useCallback, useMemo, useState } from 'react';
import { isRowBulkDeletable } from '../utils/classificationBulkDelete';

export function useClassificationBulkDelete({
  variant,
  rows,
  hierarchy,
  lowerLevelCounts = {},
  blockRecetaOrigin = false,
  deleteItem,
  onDeleted,
  setToast,
  enabled = true,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);

  const isSelectable = useCallback(
    (row) => isRowBulkDeletable(variant, row, hierarchy, lowerLevelCounts, blockRecetaOrigin),
    [variant, hierarchy, lowerLevelCounts, blockRecetaOrigin]
  );

  const deletableRows = useMemo(
    () => (rows || []).filter((row) => isSelectable(row)),
    [rows, isSelectable]
  );

  const showBulkDelete = enabled && deletableRows.length > 0;

  const selectedCount = useMemo(() => {
    let n = 0;
    for (const row of deletableRows) {
      if (selectedIds.has(row.id)) n += 1;
    }
    return n;
  }, [deletableRows, selectedIds]);

  const allDeletableSelected =
    deletableRows.length > 0 && deletableRows.every((row) => selectedIds.has(row.id));

  const toggleRow = useCallback(
    (row) => {
      if (!isSelectable(row)) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(row.id)) next.delete(row.id);
        else next.add(row.id);
        return next;
      });
    },
    [isSelectable]
  );

  const toggleSelectAll = useCallback(() => {
    if (allDeletableSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(deletableRows.map((row) => row.id)));
  }, [allDeletableSelected, deletableRows]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const openConfirm = useCallback(() => {
    if (selectedCount === 0) return;
    setConfirmOpen(true);
  }, [selectedCount]);

  const closeConfirm = useCallback(() => {
    if (executing) return;
    setConfirmOpen(false);
  }, [executing]);

  const executeBulkDelete = useCallback(async () => {
    const ids = deletableRows.filter((row) => selectedIds.has(row.id)).map((row) => row.id);
    if (ids.length === 0) return;

    setExecuting(true);
    let deleted = 0;
    const errors = [];

    for (const id of ids) {
      try {
        const res = await deleteItem(id);
        if (res?.success) deleted += 1;
        else errors.push(res?.message || `No se pudo eliminar ID ${id}`);
      } catch (e) {
        errors.push(e.response?.data?.message || e.message || `Error al eliminar ID ${id}`);
      }
    }

    setExecuting(false);
    setConfirmOpen(false);
    clearSelection();

    if (deleted > 0) await onDeleted?.();

    const type = deleted === 0 ? 'error' : errors.length > 0 ? 'warning' : 'success';
    const parts = [`Eliminados: ${deleted}.`];
    if (errors.length > 0) {
      parts.push(`Errores: ${errors.length}. ${errors.slice(0, 3).join(' ')}`);
    }
    setToast?.({ isOpen: true, message: parts.join(' '), type });
  }, [deletableRows, selectedIds, deleteItem, onDeleted, setToast, clearSelection]);

  const confirmMessage =
    selectedCount === 1
      ? '¿Eliminar el registro seleccionado? Esta acción no se puede deshacer.'
      : `¿Eliminar ${selectedCount} registros seleccionados? Esta acción no se puede deshacer.`;

  return {
    showBulkDelete,
    showColumn: showBulkDelete,
    isSelectable,
    isSelected: (id) => selectedIds.has(id),
    toggleRow,
    allDeletableSelected,
    toggleSelectAll,
    selectedCount,
    confirmOpen,
    openConfirm,
    closeConfirm,
    executing,
    executeBulkDelete,
    confirmMessage,
  };
}
